import { useEffect, useRef, useState } from 'react';
import { Money } from '@phosphor-icons/react';
import { AccentButtonOutline } from '../../../../../shared/Buttons';
import { LoanDetails, LoanStatuses, LoanType } from '../../../../../types/apiTypes';
import PayTowardAdvanceModal from './widgets/modals/PayTowardAdvanceModal';

const BUTTON_CLASS =
  'rounded-full space-x-2 justify-center flex flex-row items-center';

// Sits above the loan detail card, right-aligned to the card's width.
const WRAPPER_CLASS = 'flex w-full justify-end lg:w-[760px] lg:max-w-[760px]';

// After a payment, keep the button disabled this long so a customer can't reopen and
// re-pay before the balance reflects the first payment (the backend lock only guards
// the in-flight request, not the async settle window).
const PAYMENT_SETTLE_MS = 6000;

/**
 * "Make a Payment" CTA on the cash-advance detail screen — opens the manual-payment
 * modal. Only rendered for active cash advances with a balance still owed — fuel
 * advances aren't supported by the backend.
 */
export function PayTowardAdvanceCTA({ loan }: { loan: LoanDetails | null }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [recentlyPaid, setRecentlyPaid] = useState(false);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  useEffect(() => () => clearTimeout(settleTimer.current), []);

  const isCashAdvance = loan?.type === LoanType.CASH_ADVANCE;
  const isActive = loan?.status === LoanStatuses.Active;
  const hasBalance = (loan?.current_balance ?? 0) > 0;
  if (!loan || !isCashAdvance || !isActive || !hasBalance) return null;

  const handleSubmitted = () => {
    setRecentlyPaid(true);
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(
      () => setRecentlyPaid(false),
      PAYMENT_SETTLE_MS
    );
  };

  return (
    <div className={WRAPPER_CLASS}>
      <AccentButtonOutline
        onClick={() => setModalOpen(true)}
        disabled={recentlyPaid}
        addClass={BUTTON_CLASS}
      >
        <Money size={20} />
        <p>{recentlyPaid ? 'Processing…' : 'Make a Payment'}</p>
      </AccentButtonOutline>
      {modalOpen && (
        <PayTowardAdvanceModal
          onClose={() => setModalOpen(false)}
          onSubmitted={handleSubmitted}
          loan={loan}
        />
      )}
    </div>
  );
}

export default PayTowardAdvanceCTA;
